"use client";

import React from "react";
import styles from "./TabSection.module.css";
import Image from "next/image";
import Link from "next/link";

const BRAND = "#F48428";

function FeatureItem({ icon, title, text }) {
  return (
    <div className={`${styles.featureItem} d-flex align-items-start gap-3 mb-3`}>
      <div
        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          color: BRAND,
          background: `linear-gradient(180deg, ${BRAND}22, transparent)`,
          border: `1px solid ${BRAND}55`,
          boxShadow: `0 0 0 6px ${BRAND}10`,
        }}
      >
        <i className={icon} style={{ fontSize: 18 }}></i>
      </div>
      <div>
        <div className="fw-bold text-white" style={{ lineHeight: 1.1 }}>{title}</div>
        <div className={`${styles.featureText} small mt-1`}>{text}</div>
      </div>
    </div>
  );
}

export default function TabSection({ dynamicData }) {
  if (!dynamicData) return null;

  const { tag, title, subtitle, image, tabs = [] } = dynamicData;

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  const mid = Math.ceil(tabs.length / 2);
  const leftItems = tabs.slice(0, mid);
  const rightItems = tabs.slice(mid);

  return (
    <section className={`py-5 position-relative ${styles.softwareFeatures}`} style={{ background: "rgba(0,0,0,0)" }}>
      <div className="container">
        <div className={`text-center mb-4 ${styles.sectionHead}`}>
          <div className={`${styles.eyebrow} text-uppercase small`}>{tag || "Features"}</div>
          <h2 className={`fw-bold ${styles.sectionTitle}`}>{title}</h2>
          <p className={`${styles.subtitle} m-0`}>{subtitle}</p>
        </div>

        <div className="row g-4 align-items-center">
          {/* Left list */}
          <div className="col-12 col-lg-4">
            <div className={`pe-lg-3 ${styles.featuresCol}`}>
              {leftItems.map((f, idx) => (
                <FeatureItem key={idx} icon={f.icon} title={f.name} text={f.description} />
              ))}
            </div>
          </div>

          {/* Center visual */}
          <div className="col-12 col-lg-4 d-flex justify-content-center">
            <div className={styles.tray} data-reveal="zoom">
              <div className={`${styles.phone} ${styles.left}`}>
                <span className={styles.notch} aria-hidden></span>
                <div className={styles.screen}>
                  <Image
                    src={getImageUrl(image)}
                    alt="app preview left"
                    className={styles.mainImage}
                    style={{ objectFit: "cover" }}
                    fill
                  />
                </div>
              </div>
              <div className={`${styles.phone} ${styles.right}`}>
                <span className={styles.notch} aria-hidden></span>
                <div className={styles.screen}>
                  <Image
                    src={getImageUrl(image)}
                    alt="app preview right"
                    className={styles.mainImage}
                    style={{ objectFit: "cover" }}
                    fill
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right list */}
          <div className="col-12 col-lg-4">
            <div className={`ps-lg-3 ${styles.featuresCol}`}>
              {rightItems.map((f, idx) => (
                <FeatureItem key={idx} icon={f.icon} title={f.name} text={f.description} />
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-4">
          <Link href="/contact" className={styles.ctaBtn} aria-label="Contact our experts">
            Talk to Our Experts
            <i className="fa-solid fa-arrow-right ms-2" aria-hidden></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
